const KLAVIYO_REVISION = '2026-07-15';
const KLAVIYO_WAIT_TIMEOUT = 10000;
const LOCAL_DEV_SITE_ID = 'LOCAL1';
const PREVIEW_STATE_PARAM = 'klaviyo_state';
const PREVIEW_STATES = new Set(['new_visitor', 'welcome_discount', 'purchased', 'active_subscriber']);
const membershipCache = new Map();
const invalidFeedbackTimers = new WeakMap();
const errorHideTimers = new WeakMap();
const copyFeedbackTimers = new WeakMap();
const INVALID_FEEDBACK_MS = 900;
const ERROR_POP_MS = 700;
const SUCCESS_HOLD_MS = 560;
const SUCCESS_FADE_MS = 280;
const LABEL_POP_MS = 280;
const COPIED_HOLD_MS = 1000;
// Set after a sign-up here; Klaviyo popups that open later in this browser are closed (closePopupAfterSignup)
const POPUP_GUARD_KEY = 'dynamic-klaviyo:popup-off';
let popupGuardFallback = false;

/**
 * @typedef {{
 *   getGroupMembership: (groupIds: string[]) => Promise<string[]>,
 *   identify?: (profile: { email: string }) => Promise<unknown>
 * }} KlaviyoClient
 */

/**
 * @typedef {'new_visitor' | 'welcome_discount' | 'purchased' | 'active_subscriber'} KlaviyoState
 */

/**
 * @returns {KlaviyoClient | undefined}
 */
function getKlaviyoClient() {
  return /** @type {Window & typeof globalThis & { klaviyo?: KlaviyoClient }} */ (window).klaviyo;
}

/**
 * @param {unknown} value
 * @returns {string | null}
 */
function normalizeSiteId(value) {
  const trimmed = String(value || '').trim();
  if (/^[A-Za-z0-9]{6}$/.test(trimmed)) return trimmed;

  const match = trimmed.match(/^pk_([A-Za-z0-9]{6})_/);
  return match?.[1] || null;
}

/**
 * @param {string[]} values
 * @returns {string[]}
 */
function uniqueGroupIds(values) {
  return [...new Set(values.filter(Boolean))];
}

/**
 * @param {string[]} groupIds
 * @returns {string}
 */
function membershipKey(groupIds) {
  return [...groupIds].sort().join(',');
}

/**
 * Reject if Klaviyo queues the lookup and never settles it.
 * @param {Promise<Set<string>>} promise
 * @param {number} timeout
 * @returns {Promise<Set<string>>}
 */
function withTimeout(promise, timeout) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('Klaviyo onsite tracking did not become available.'));
    }, timeout);

    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * Klaviyo's onsite snippet installs `window.klaviyo` as a Proxy whose `get`
 * trap returns a function for every property, including `then`. Passing that
 * object to `Promise.resolve()` (or `resolve(client)`) makes the promise adopt
 * it as a thenable, which Klaviyo records as an event named "then" and never
 * settles. The section then stays `hidden` forever. Box the client so the
 * promise resolves to a plain object.
 *
 * @param {KlaviyoClient} client
 */
function boxKlaviyoClient(client) {
  return { client };
}

/**
 * @param {number} [timeout]
 * @returns {Promise<{ client: KlaviyoClient }>}
 */
function waitForKlaviyo(timeout = KLAVIYO_WAIT_TIMEOUT) {
  const readyClient = () => {
    const client = getKlaviyoClient();
    return typeof client?.getGroupMembership === 'function' ? client : null;
  };

  const existingClient = readyClient();
  if (existingClient) return Promise.resolve(boxKlaviyoClient(existingClient));

  return new Promise((resolve, reject) => {
    const startedAt = performance.now();
    const timer = window.setInterval(() => {
      const client = readyClient();
      if (client) {
        window.clearInterval(timer);
        resolve(boxKlaviyoClient(client));
        return;
      }

      if (performance.now() - startedAt >= timeout) {
        window.clearInterval(timer);
        reject(new Error('Klaviyo onsite tracking did not become available.'));
      }
    }, 100);
  });
}

/**
 * @param {string[]} groupIds
 * @param {boolean} [force]
 * @returns {Promise<Set<string>>}
 */
function getMembership(groupIds, force = false) {
  const key = membershipKey(groupIds);

  if (force) membershipCache.delete(key);
  if (membershipCache.has(key)) return membershipCache.get(key);

  const request = withTimeout(
    waitForKlaviyo()
      .then(({ client }) => client.getGroupMembership(groupIds))
      .then((memberships) => {
        if (!Array.isArray(memberships)) {
          throw new TypeError('Klaviyo returned an invalid group membership response.');
        }

        return new Set(memberships);
      }),
    KLAVIYO_WAIT_TIMEOUT
  );

  membershipCache.set(key, request);
  return request;
}

/**
 * @param {string} listId
 * @returns {string}
 */
function signupStorageKey(listId) {
  return `dynamic-klaviyo:joined:${listId}`;
}

/**
 * @param {string} listId
 * @returns {boolean}
 */
function hasLocalSignup(listId) {
  try {
    return sessionStorage.getItem(signupStorageKey(listId)) === 'true';
  } catch {
    return false;
  }
}

/**
 * @param {string} listId
 */
function storeLocalSignup(listId) {
  try {
    sessionStorage.setItem(signupStorageKey(listId), 'true');
  } catch {
    // Membership still refreshes through Klaviyo when storage is unavailable.
  }
  try {
    localStorage.setItem(POPUP_GUARD_KEY, 'true');
  } catch {
    // Without storage the popup guard only covers this page.
    popupGuardFallback = true;
  }
}

/**
 * @returns {boolean} Whether this browser has signed up through this section
 */
function hasSignedUpHere() {
  if (popupGuardFallback) return true;
  try {
    return localStorage.getItem(POPUP_GUARD_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Someone who signed up in this section never needs Klaviyo's sign-up popup. Klaviyo only stops showing it once
 * it has identified the visitor on a later page, so until then (and the exit-intent popup straight after
 * signing up here) the popup is shut through its own close button as soon as it opens. Klaviyo records that
 * as an ordinary close.
 *
 * @param {Event} event
 */
function closePopupAfterSignup(event) {
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (detail?.type !== 'open' || !hasSignedUpHere()) return;

  // The popup's markup can land a frame after the event
  let attempts = 0;
  const close = () => {
    const button = /** @type {HTMLElement | null} */ (
      document.querySelector('[role="dialog"] .klaviyo-close-form')
    );
    if (button) {
      button.click();
      return;
    }
    if (++attempts < 30) requestAnimationFrame(close);
  };
  close();
}

window.addEventListener('klaviyoForms', closePopupAfterSignup);

/**
 * @param {string} value
 */
async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const field = document.createElement('textarea');
  field.value = value;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.append(field);
  field.select();
  document.execCommand('copy');
  field.remove();
}

class DynamicKlaviyoSection extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedInitialized) return;
    this.isConnectedInitialized = true;

    this.addEventListener('submit', this.handleSubmit);
    this.addEventListener('click', this.handleClick);
    window.addEventListener('dynamic-klaviyo:signed-up', this.handleExternalSignup);
    window.addEventListener('klaviyoForms', this.handleKlaviyoFormEvent);

    if (this.dataset.designMode === 'true') {
      this.showEditorState();
      return;
    }

    this.resolveState();
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.handleSubmit);
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('dynamic-klaviyo:signed-up', this.handleExternalSignup);
    window.removeEventListener('klaviyoForms', this.handleKlaviyoFormEvent);
    this.isConnectedInitialized = false;
  }

  get groupIds() {
    return {
      welcome: this.dataset.welcomeGroupId?.trim() || '',
      purchased: this.dataset.purchasedGroupId?.trim() || '',
      subscriber: this.dataset.subscriberGroupId?.trim() || '',
    };
  }

  /**
   * Lets `?klaviyo_state=new_visitor` force a state on the storefront, where the section
   * otherwise stays hidden without Klaviyo onsite tracking. Requires a deliberate URL
   * param, so normal visitors are never affected.
   *
   * @returns {KlaviyoState | null}
   */
  get previewState() {
    const value = new URLSearchParams(window.location.search).get(PREVIEW_STATE_PARAM);
    return PREVIEW_STATES.has(value || '') ? /** @type {KlaviyoState} */ (value) : null;
  }

  get contentBlocks() {
    return [
      .../** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-dynamic-klaviyo-rule]')),
    ];
  }

  showEditorState() {
    this.hidden = false;
    this.dataset.klaviyoState = 'editor';
    this.contentBlocks.forEach((block) => {
      block.hidden = false;
    });
  }

  async resolveState(force = false) {
    const previewState = this.previewState;
    if (previewState) {
      this.renderState(previewState);
      return;
    }

    const ids = this.groupIds;
    const groupIds = uniqueGroupIds([ids.welcome, ids.purchased, ids.subscriber]);

    /*
     * Welcome list is required for signup → discount handoff. Purchased /
     * subscriber segments are optional — empty used to failClosed the whole
     * section, which hid the Nundies landing embed entirely.
     */
    if (!ids.welcome) {
      this.failClosed(
        `Dynamic Klaviyo Section is missing the welcome list ID. ` +
          `Add it in the section settings, or append ?${PREVIEW_STATE_PARAM}=new_visitor to preview a state.`
      );
      return;
    }

    if (!groupIds.length) {
      this.failClosed('Dynamic Klaviyo Section has no group IDs to check.');
      return;
    }

    try {
      const memberships = await getMembership(groupIds, force);
      /** @type {KlaviyoState} */
      let state = 'new_visitor';

      if (ids.subscriber && memberships.has(ids.subscriber)) {
        state = 'active_subscriber';
      } else if (ids.purchased && memberships.has(ids.purchased)) {
        state = 'purchased';
      } else if (memberships.has(ids.welcome) || hasLocalSignup(ids.welcome)) {
        state = 'welcome_discount';
      }

      this.renderState(state);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      /*
       * Ad blockers and a disabled Klaviyo onsite embed never expose
       * getGroupMembership. Hiding the section in that case blanks the
       * signup the merchant already configured. Show the new-visitor block
       * instead. A real membership rejection still fail-closes.
       */
      if (message.includes('did not become available')) {
        console.warn(
          '[dynamic-klaviyo]',
          error,
          'Showing the new-visitor signup because onsite tracking did not load.'
        );
        this.renderState('new_visitor');
        return;
      }
      this.failClosed(error);
    }
  }

  /**
   * @param {KlaviyoState} state
   */
  renderState(state) {
    this.dataset.klaviyoState = state;

    if (state === 'active_subscriber') {
      this.contentBlocks.forEach((block) => {
        block.hidden = true;
      });
      this.hidden = true;
      return;
    }

    let visibleBlocks = 0;
    this.contentBlocks.forEach((block) => {
      const isVisible = block.dataset.dynamicKlaviyoRule === state || block.dataset.dynamicKlaviyoRule === 'always';
      block.hidden = !isVisible;
      if (isVisible) visibleBlocks += 1;
    });

    this.hidden = visibleBlocks === 0;
  }

  /**
   * @param {unknown} error
   */
  failClosed(error) {
    this.dataset.klaviyoState = 'unavailable';
    this.contentBlocks.forEach((block) => {
      block.hidden = true;
    });
    this.hidden = true;
    console.warn(
      '[dynamic-klaviyo]',
      error,
      `Section hidden. Append ?${PREVIEW_STATE_PARAM}=new_visitor to the URL to preview a state without Klaviyo.`
    );
  }

  /**
   * Klaviyo embed forms (`.klaviyo-form-{id}`) fire `klaviyoForms` on submit.
   * Mirror the custom form handoff so the discount block appears.
   *
   * @param {Event} event
   */
  handleKlaviyoFormEvent = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || detail.type !== 'submit') return;

    const formId = String(detail.formId || detail.formID || '').trim();
    if (!formId) return;

    const embeds = [
      .../** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-klaviyo-embed-form]')),
    ];
    const matched = embeds.some(
      (embed) =>
        embed.dataset.klaviyoFormId === formId || embed.classList.contains(`klaviyo-form-${formId}`)
    );
    if (!matched) return;

    const listId = this.groupIds.welcome;
    if (!listId) return;

    storeLocalSignup(listId);
    membershipCache.clear();
    window.dispatchEvent(new CustomEvent('dynamic-klaviyo:signed-up', { detail: { listId } }));
  };

  /**
   * @param {Event} event
   */
  handleExternalSignup = (event) => {
    if (!(event instanceof CustomEvent)) return;
    if (event.detail?.listId !== this.groupIds.welcome) return;
    if (event.detail?.source === this) return;
    if (this.dataset.klaviyoState === 'welcome_discount') return;
    this.revealDiscount({ animate: this.dataset.klaviyoState === 'new_visitor' });
  };

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    if (!(event.target instanceof Element)) return;

    const form = event.target.closest('[data-klaviyo-signup-form]');
    if (!form || !this.contains(form)) return;

    event.preventDefault();
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.submitting === 'true') return;

    const emailInput = form.querySelector('input[type="email"]');
    const errorMessage = form.querySelector('[data-klaviyo-form-error]');
    const submitButton = form.querySelector('button[type="submit"]');
    if (
      !(emailInput instanceof HTMLInputElement) ||
      !(errorMessage instanceof HTMLElement) ||
      !(submitButton instanceof HTMLButtonElement)
    ) {
      return;
    }

    const email = emailInput?.value.trim() || '';

    if (!emailInput?.checkValidity()) {
      emailInput?.setAttribute('aria-invalid', 'true');
      showFormError(errorMessage, form.dataset.invalidEmailMessage || '');
      playSignupInvalid(form);
      emailInput?.focus();
      return;
    }

    const siteId = normalizeSiteId(this.dataset.siteId);
    const listId = this.groupIds.welcome;

    if (!siteId || !listId) {
      showFormError(errorMessage, form.dataset.configErrorMessage || '');
      return;
    }

    emailInput.removeAttribute('aria-invalid');
    hideFormError(errorMessage);
    form.dataset.submitting = 'true';
    form.setAttribute('aria-busy', 'true');
    submitButton.disabled = true;

    try {
      if (siteId === LOCAL_DEV_SITE_ID) {
        await new Promise((resolve) => window.setTimeout(resolve, 250));
      } else {
        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(siteId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: KLAVIYO_REVISION,
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: form.dataset.customSource,
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                        subscriptions: {
                          email: {
                            marketing: {
                              consent: 'SUBSCRIBED',
                            },
                          },
                        },
                      },
                    },
                  },
                },
                relationships: {
                  list: {
                    data: {
                      type: 'list',
                      id: listId,
                    },
                  },
                },
              },
            }),
          }
        );

        if (!response.ok && response.status !== 202) {
          throw new Error(`Klaviyo subscription failed with status ${response.status}.`);
        }
      }

      storeLocalSignup(listId);
      membershipCache.clear();
      getKlaviyoClient()?.identify?.({ email }).catch(() => {});
      playFieldSuccess(form);
      if (!prefersReducedMotion()) await wait(SUCCESS_HOLD_MS);
      clearFieldSuccess(form);
      if (!prefersReducedMotion()) await wait(SUCCESS_FADE_MS);
      await this.revealDiscount({ animate: true });
      window.dispatchEvent(
        new CustomEvent('dynamic-klaviyo:signed-up', { detail: { listId, source: this } })
      );
    } catch (error) {
      const isNetworkError = error instanceof TypeError;
      showFormError(
        errorMessage,
        isNetworkError ? form.dataset.networkErrorMessage || '' : form.dataset.errorMessage || ''
      );
      console.error('[dynamic-klaviyo]', error);
    } finally {
      form.dataset.submitting = 'false';
      form.setAttribute('aria-busy', 'false');
      submitButton.disabled = false;
    }
  };

  /**
   * @param {MouseEvent} event
   */
  handleClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const copyButton = event.target.closest('[data-klaviyo-copy]');
    if (!copyButton || !this.contains(copyButton)) return;
    if (!(copyButton instanceof HTMLButtonElement)) return;

    const originalLabel = copyButton.dataset.copyLabel || '';
    const code = copyButton.dataset.klaviyoCopy || '';
    const labelEl = copyButton.querySelector('.nundies-button__label') || copyButton;
    const field = copyButton.closest('.dynamic-klaviyo-form__field') || copyButton;
    if (copyButton.dataset.copying === 'true') return;
    copyButton.dataset.copying = 'true';

    const previous = copyFeedbackTimers.get(copyButton);
    if (previous) window.clearTimeout(previous);

    try {
      await copyText(code);
      playFieldSuccess(field);
      await swapLabel(labelEl, copyButton.dataset.copiedLabel || originalLabel);
      const timer = window.setTimeout(async () => {
        await swapLabel(labelEl, originalLabel);
        clearFieldSuccess(field);
        copyButton.dataset.copying = 'false';
        copyFeedbackTimers.delete(copyButton);
      }, COPIED_HOLD_MS);
      copyFeedbackTimers.set(copyButton, timer);
    } catch (error) {
      copyButton.dataset.copying = 'false';
      console.error('[dynamic-klaviyo]', error);
    }
  };

  /**
   * @param {{ animate?: boolean }} [options]
   */
  async revealDiscount(options = {}) {
    const animate = Boolean(options.animate) && !prefersReducedMotion();
    if (!animate) {
      this.renderState('welcome_discount');
      return;
    }

    const outgoing = this.contentBlocks.filter(
      (block) => !block.hidden && block.dataset.dynamicKlaviyoRule === 'new_visitor'
    );
    const incoming = this.contentBlocks.filter(
      (block) => block.dataset.dynamicKlaviyoRule === 'welcome_discount'
    );

    await Promise.all(outgoing.flatMap(blockMotionRoots).map(popOff));
    incoming.flatMap(blockMotionRoots).forEach((root) => {
      root.classList.add('is-poppable');
      root.classList.remove('is-animated');
    });
    this.renderState('welcome_discount');
    await Promise.all(
      incoming.filter((block) => !block.hidden).flatMap(blockMotionRoots).map(popOnPrepared)
    );
  }
}

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * @param {HTMLElement} block
 * @returns {HTMLElement[]}
 */
function blockMotionRoots(block) {
  const content = block.querySelector('.dynamic-klaviyo-block__content');
  if (content instanceof HTMLElement) return [content];

  return [block.querySelector('.dynamic-klaviyo-block__copy'), block.querySelector('.dynamic-klaviyo-block__action')].filter(
    (node) => node instanceof HTMLElement
  );
}

/**
 * @param {HTMLElement} element
 * @returns {Promise<void>}
 */
async function popOff(element) {
  if (prefersReducedMotion()) return;

  element.classList.add('is-poppable', 'is-animated');
  element.style.willChange = 'transform, opacity';
  void element.offsetWidth;
  element.classList.remove('is-animated');
  await wait(ERROR_POP_MS);
  element.classList.remove('is-poppable');
  element.style.willChange = 'auto';
}

/**
 * @param {HTMLElement} element
 * @returns {Promise<void>}
 */
async function popOnPrepared(element) {
  if (prefersReducedMotion()) {
    element.classList.remove('is-poppable', 'is-animated');
    return;
  }

  element.style.willChange = 'transform, opacity';
  await new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        element.classList.add('is-animated');
        resolve();
      });
    });
  });
  await wait(ERROR_POP_MS);
  element.classList.remove('is-poppable', 'is-animated');
  element.style.willChange = 'auto';
}

/**
 * @param {Element} root
 */
function playFieldSuccess(root) {
  const form = root instanceof HTMLFormElement ? root : root.closest('.dynamic-klaviyo-form');
  const field =
    root instanceof HTMLElement && root.classList.contains('dynamic-klaviyo-form__field')
      ? root
      : root.querySelector?.('.dynamic-klaviyo-form__field') || root.closest('.dynamic-klaviyo-form__field');
  const reveal = (field || root).querySelector?.('.nundies-button-reveal');
  const button = (field || root).querySelector?.('.nundies-button');

  form?.classList.remove('is-invalid');
  form?.classList.add('is-success');
  if (field instanceof HTMLElement) field.classList.add('is-success');
  if (reveal instanceof HTMLElement) {
    reveal.classList.remove('is-invalid');
    reveal.classList.add('is-success');
  }
  if (button instanceof HTMLElement) {
    button.classList.remove('is-invalid');
    button.classList.add('is-success');
  }
}

/**
 * @param {Element} root
 */
function clearFieldSuccess(root) {
  const form = root instanceof HTMLFormElement ? root : root.closest('.dynamic-klaviyo-form');
  const field =
    root instanceof HTMLElement && root.classList.contains('dynamic-klaviyo-form__field')
      ? root
      : root.querySelector?.('.dynamic-klaviyo-form__field') || root.closest('.dynamic-klaviyo-form__field');
  const reveal = (field || root).querySelector?.('.nundies-button-reveal');
  const button = (field || root).querySelector?.('.nundies-button');

  form?.classList.remove('is-success');
  if (field instanceof HTMLElement) field.classList.remove('is-success');
  if (reveal instanceof HTMLElement) reveal.classList.remove('is-success');
  if (button instanceof HTMLElement) button.classList.remove('is-success');
}

/**
 * @param {Element} labelEl
 * @param {string} text
 * @returns {Promise<void>}
 */
async function swapLabel(labelEl, text) {
  if (!(labelEl instanceof HTMLElement)) return;
  if (labelEl.textContent === text) return;

  if (prefersReducedMotion()) {
    labelEl.textContent = text;
    return;
  }

  labelEl.classList.add('is-popping');
  await wait(LABEL_POP_MS);
  labelEl.textContent = text;
  void labelEl.offsetWidth;
  labelEl.classList.remove('is-popping');
  await wait(LABEL_POP_MS);
}

/**
 * @param {HTMLElement} errorMessage
 */
function prefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * @param {HTMLElement} errorMessage
 * @param {string} text
 */
function showFormError(errorMessage, text) {
  const previous = errorHideTimers.get(errorMessage);
  if (previous) window.clearTimeout(previous);

  errorMessage.textContent = text;
  errorMessage.hidden = false;

  if (prefersReducedMotion()) {
    errorMessage.classList.add('is-animated');
    return;
  }

  if (errorMessage.classList.contains('is-animated')) return;

  errorMessage.style.willChange = 'transform, opacity';
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      errorMessage.classList.add('is-animated');
    });
  });
}

/**
 * @param {HTMLElement} errorMessage
 */
function hideFormError(errorMessage) {
  const previous = errorHideTimers.get(errorMessage);
  if (previous) window.clearTimeout(previous);

  if (errorMessage.hidden && !errorMessage.classList.contains('is-animated')) return;

  const finish = () => {
    errorMessage.classList.remove('is-animated');
    errorMessage.hidden = true;
    errorMessage.style.willChange = 'auto';
    errorHideTimers.delete(errorMessage);
  };

  if (prefersReducedMotion() || !errorMessage.classList.contains('is-animated')) {
    finish();
    return;
  }

  errorMessage.classList.remove('is-animated');
  const timer = window.setTimeout(finish, ERROR_POP_MS);
  errorHideTimers.set(errorMessage, timer);
}

/**
 * @param {HTMLFormElement} form
 */
function playSignupInvalid(form) {
  const reveal = form.querySelector('.nundies-button-reveal');
  const submitButton = form.querySelector('button[type="submit"]');
  const motionRoot = reveal instanceof HTMLElement ? reveal : submitButton;
  if (!(motionRoot instanceof HTMLElement)) return;

  const previous = invalidFeedbackTimers.get(form);
  if (previous) window.clearTimeout(previous);

  form.classList.remove('is-invalid');
  motionRoot.classList.remove('is-invalid');
  if (submitButton instanceof HTMLElement) submitButton.classList.remove('is-invalid');
  void motionRoot.offsetWidth;

  form.classList.add('is-invalid');
  motionRoot.classList.add('is-invalid');
  if (submitButton instanceof HTMLElement) submitButton.classList.add('is-invalid');

  const timer = window.setTimeout(() => {
    form.classList.remove('is-invalid');
    motionRoot.classList.remove('is-invalid');
    if (submitButton instanceof HTMLElement) submitButton.classList.remove('is-invalid');
    invalidFeedbackTimers.delete(form);
  }, INVALID_FEEDBACK_MS);
  invalidFeedbackTimers.set(form, timer);
}

if (!customElements.get('dynamic-klaviyo-section')) {
  customElements.define('dynamic-klaviyo-section', DynamicKlaviyoSection);
}
